"use client";

import {
  Dialog,
  DialogActions,
  DialogDescription,
  DialogTitle,
} from "@/components/catalyst/dialog";
import {
  deleteEvent,
  deleteService,
  deleteDigitalProduct,
} from "@/app/actions";
import { Button } from "@/components/catalyst/button";
import { ProductType } from "@/lib/entities";
import { useTranslations } from "next-intl";
import { useState } from "react";

interface DeleteProductDialogProps {
  productType: ProductType;
  productSlug: string;
  creatorSlug: string;
  productTitle: string;
  isOpen: boolean;
  onClose: () => void;
}

export function DeleteProductDialog({
  productType,
  productSlug,
  creatorSlug,
  productTitle,
  isOpen,
  onClose,
}: DeleteProductDialogProps) {
  const t = useTranslations("DeleteProductDialog");
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    setIsDeleting(true);
    setError(null);

    let result: { error: string } | void;

    if (productType === "event") {
      result = await deleteEvent(productSlug, creatorSlug);
    } else if (productType === "service") {
      result = await deleteService(productSlug, creatorSlug);
    } else {
      result = await deleteDigitalProduct(productSlug, creatorSlug);
    }

    if (result?.error) {
      setError(result.error);
      setIsDeleting(false);
    }
  };

  const productTypeLabel =
    productType === "event"
      ? t("deleteEvent")
      : productType === "service"
        ? t("deleteService")
        : t("deleteDigitalProduct");

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <div>
        <DialogTitle>{productTypeLabel}</DialogTitle>
        <DialogDescription>
          {t("confirmDelete", { title: productTitle })}
        </DialogDescription>

        {error && (
          <div
            className="rounded-md bg-red-50 p-4 dark:bg-red-900/20"
            data-testid={`delete-dialog-error-${productSlug}`}
          >
            <div className="text-sm text-red-700 dark:text-red-200">
              {error}
            </div>
          </div>
        )}

        <DialogActions>
          <Button
            outline
            onClick={onClose}
            disabled={isDeleting}
            data-testid={`delete-dialog-cancel-${productSlug}`}
          >
            {t("cancel")}
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            disabled={isDeleting}
            data-testid={`delete-dialog-confirm-${productSlug}`}
          >
            {isDeleting
              ? t("deleting")
              : t("delete", { type: productTypeLabel })}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
